const ALGO = 'AES-GCM';
const VERSION = 1;

function bytesToBase64(bytes) {
  let binary = '';
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i]);
  return btoa(binary);
}

function base64ToBytes(value) {
  const binary = atob(String(value || ''));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function aadFor({ entryId, clientId, kind }) {
  return new TextEncoder().encode(
    JSON.stringify({ v: VERSION, entryId: String(entryId), clientId, kind })
  );
}

async function importVaultKey(vaultKey) {
  if (vaultKey instanceof CryptoKey) return vaultKey;
  const raw = vaultKey instanceof Uint8Array ? vaultKey : new Uint8Array(vaultKey);
  return crypto.subtle.importKey('raw', raw, { name: ALGO }, false, ['encrypt', 'decrypt']);
}

async function encryptBytes(key, bytes, aad) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGO, iv, additionalData: aad, tagLength: 128 },
    key,
    bytes
  );
  return { ciphertext: new Uint8Array(ciphertext), iv: bytesToBase64(iv) };
}

async function decryptBytes(key, ciphertext, iv, aad) {
  return crypto.subtle.decrypt(
    { name: ALGO, iv: base64ToBytes(iv), additionalData: aad, tagLength: 128 },
    key,
    ciphertext
  );
}

async function makeImageDerivative(file, maxWidth, quality) {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas is unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Image derivative failed'))),
        'image/jpeg',
        quality
      );
    });
  } finally {
    bitmap.close();
  }
}

async function importFileKey(rawFileKey, usages) {
  return crypto.subtle.importKey('raw', rawFileKey, { name: ALGO }, false, usages);
}

async function unwrapFileKey({ entryId, metadata, vaultKey }) {
  const vaultCryptoKey = await importVaultKey(vaultKey);
  const rawFileKey = await decryptBytes(
    vaultCryptoKey,
    base64ToBytes(metadata.wrappedFileKey),
    metadata.wrappedFileKeyIv,
    aadFor({ entryId, clientId: metadata.clientId, kind: 'file-key' })
  );
  return importFileKey(rawFileKey, ['decrypt']);
}

export async function encryptSensitiveAttachment({ entryId, file, vaultKey }) {
  if (!vaultKey) throw new Error('Unlock the Sensitive Vault before uploading attachments');

  const vaultCryptoKey = await importVaultKey(vaultKey);
  const fileKey = await crypto.subtle.generateKey({ name: ALGO, length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const rawFileKey = new Uint8Array(await crypto.subtle.exportKey('raw', fileKey));
  const clientId = crypto.randomUUID();

  const wrapped = await encryptBytes(
    vaultCryptoKey,
    rawFileKey,
    aadFor({ entryId, clientId, kind: 'file-key' })
  );

  const original = await encryptBytes(
    fileKey,
    new Uint8Array(await file.arrayBuffer()),
    aadFor({ entryId, clientId, kind: 'original' })
  );

  let thumb = null;
  let preview = null;
  if (String(file.type || '').startsWith('image/')) {
    const [thumbBlob, previewBlob] = await Promise.all([
      makeImageDerivative(file, 320, 0.78),
      makeImageDerivative(file, 1600, 0.82),
    ]);
    thumb = await encryptBytes(
      fileKey,
      new Uint8Array(await thumbBlob.arrayBuffer()),
      aadFor({ entryId, clientId, kind: 'thumb' })
    );
    preview = await encryptBytes(
      fileKey,
      new Uint8Array(await previewBlob.arrayBuffer()),
      aadFor({ entryId, clientId, kind: 'preview' })
    );
  }

  const privateMetadata = new TextEncoder().encode(
    JSON.stringify({
      originalName: file.uploadName || file.name || 'attachment',
      originalMime: file.type || 'application/octet-stream',
      originalSize: Number(file.size || 0),
    })
  );
  const encryptedMetadata = await encryptBytes(
    fileKey,
    privateMetadata,
    aadFor({ entryId, clientId, kind: 'metadata' })
  );

  return {
    originalFile: new File([original.ciphertext], `${clientId}.bin`, {
      type: 'application/octet-stream',
    }),
    thumbFile: thumb
      ? new File([thumb.ciphertext], `${clientId}.thumb.bin`, {
          type: 'application/octet-stream',
        })
      : null,
    previewFile: preview
      ? new File([preview.ciphertext], `${clientId}.preview.bin`, {
          type: 'application/octet-stream',
        })
      : null,
    metadata: {
      version: VERSION,
      clientId,
      wrappedFileKey: bytesToBase64(wrapped.ciphertext),
      wrappedFileKeyIv: wrapped.iv,
      originalIv: original.iv,
      thumbIv: thumb?.iv || null,
      previewIv: preview?.iv || null,
      metadataIv: encryptedMetadata.iv,
      encryptedMetadata: bytesToBase64(encryptedMetadata.ciphertext),
      hasThumb: Boolean(thumb),
      hasPreview: Boolean(preview),
    },
  };
}

export async function decryptSensitiveAttachmentMetadata({ entryId, metadata, vaultKey }) {
  if (!vaultKey) throw new Error('Sensitive Vault is locked');
  const fileKey = await unwrapFileKey({ entryId, metadata, vaultKey });
  const plaintext = await decryptBytes(
    fileKey,
    base64ToBytes(metadata.encryptedMetadata),
    metadata.metadataIv,
    aadFor({ entryId, clientId: metadata.clientId, kind: 'metadata' })
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}

export async function decryptSensitiveAttachmentBlob({
  entryId,
  metadata,
  kind,
  ciphertextBlob,
  vaultKey,
}) {
  if (!vaultKey) throw new Error('Sensitive Vault is locked');
  const fileKey = await unwrapFileKey({ entryId, metadata, vaultKey });
  const ivField =
    kind === 'thumb' ? 'thumbIv' : kind === 'preview' ? 'previewIv' : 'originalIv';
  const plaintext = await decryptBytes(
    fileKey,
    await ciphertextBlob.arrayBuffer(),
    metadata[ivField],
    aadFor({ entryId, clientId: metadata.clientId, kind })
  );
  const privateMetadata = await decryptSensitiveAttachmentMetadata({
    entryId,
    metadata,
    vaultKey,
  });
  const mime = kind === 'original' ? privateMetadata.originalMime : 'image/jpeg';
  return new Blob([plaintext], { type: mime });
}
